// Ruling Configuration System Utilities
// This module provides core functionality for the flexible business rules system

// Supported operators for numeric scorecards (1 & 2)
export const NUMERIC_OPERATORS = {
  LESS_THAN: '<',
  LESS_THAN_EQUAL: '<=',
  GREATER_THAN: '>',
  GREATER_THAN_EQUAL: '>=',
  EQUAL: '==',
  NOT_EQUAL: '!=',
  BETWEEN: 'between',
  NOT_BETWEEN: 'not_between'
};

// Supported operators for match-based scorecards (3 & 4)
export const MATCH_OPERATORS = {
  EQUALS: 'equals',
  NOT_EQUALS: 'not_equals',
  IN: 'in',
  NOT_IN: 'not_in'
};

// Logical operators
export const LOGICAL_OPERATORS = {
  AND: 'AND',
  OR: 'OR'
};

// Rule outcomes
export const RULE_OUTCOMES = {
  PASS: 'pass',
  REFER: 'refer',
  FAIL: 'fail',
  OTHER: 'other'
};

// Scorecard types
export const SCORECARD_TYPES = {
  NUMERIC: 'numeric',
  MATCH: 'match'
};

// Default ruling configuration
export const DEFAULT_RULING_CONFIG = {
  name: "Default Configuration",
  description: "System default ruling configuration",
  version: "1.0.0",
  is_active: true,
  is_default: true,
  rules: {
    pass: {
      conditions: [
        {
          scorecard: "scorecard_1",
          operator: "<",
          value: 4,
          type: "numeric"
        },
        {
          scorecard: "scorecard_2", 
          operator: "<",
          value: 5,
          type: "numeric"
        },
        {
          scorecard: "scorecard_3",
          operator: "equals",
          value: "no_match",
          type: "match"
        },
        {
          scorecard: "scorecard_4",
          operator: "equals", 
          value: "no_match",
          type: "match"
        }
      ],
      logic: "AND"
    },
    refer: {
      conditions: [
        {
          group: [
            {
              scorecard: "scorecard_1",
              operator: "==",
              value: 4,
              type: "numeric"
            },
            {
              scorecard: "scorecard_2",
              operator: "==", 
              value: 5,
              type: "numeric"
            }
          ],
          group_logic: "OR"
        },
        {
          scorecard: "scorecard_3",
          operator: "equals",
          value: "no_match", 
          type: "match"
        },
        {
          scorecard: "scorecard_4",
          operator: "equals",
          value: "no_match",
          type: "match"
        }
      ],
      logic: "AND"
    },
    fail: {
      conditions: [
        {
          group: [
            {
              scorecard: "scorecard_1",
              operator: ">",
              value: 4,
              type: "numeric"
            },
            {
              scorecard: "scorecard_2",
              operator: ">",
              value: 5,
              type: "numeric"
            }
          ],
          group_logic: "OR"
        },
        {
          scorecard: "scorecard_3",
          operator: "equals",
          value: "match",
          type: "match"
        },
        {
          scorecard: "scorecard_4",
          operator: "equals", 
          value: "match",
          type: "match"
        }
      ],
      logic: "AND"
    }
  },
  evaluation_order: ["pass", "refer", "fail"],
  default_outcome: "other"
};

/**
 * Utility class for handling ruling configurations
 */
export class RulingConfigUtils {
  /**
   * Check if a scorecard uses match-based scoring
   */
  static isMatchBased(scorecardNum) {
    return scorecardNum === 3 || scorecardNum === 4;
  }

  /**
   * Get scorecard type
   */
  static getScorecardType(scorecardNum) {
    return this.isMatchBased(scorecardNum) ? SCORECARD_TYPES.MATCH : SCORECARD_TYPES.NUMERIC;
  }

  /**
   * Get available operators for a scorecard
   */
  static getAvailableOperators(scorecardNum) {
    return this.isMatchBased(scorecardNum) ? MATCH_OPERATORS : NUMERIC_OPERATORS;
  }

  /**
   * Validate a single condition
   */
  static validateCondition(condition) {
    if (!condition.scorecard) {
      return "Scorecard is required";
    }

    if (!condition.operator) {
      return "Operator is required";
    }

    if (!condition.type) {
      return "Type is required";
    }

    const scorecardNum = parseInt(condition.scorecard.split('_')[1]);
    const expectedType = this.getScorecardType(scorecardNum);
    
    if (condition.type !== expectedType) {
      return `Type mismatch: expected ${expectedType} for ${condition.scorecard}`;
    }

    const availableOperators = Object.values(this.getAvailableOperators(scorecardNum));
    if (!availableOperators.includes(condition.operator)) {
      return `Invalid operator ${condition.operator} for ${condition.scorecard}`;
    }

    // Validate value based on operator
    if (condition.operator === NUMERIC_OPERATORS.BETWEEN || condition.operator === NUMERIC_OPERATORS.NOT_BETWEEN) {
      if (condition.min === undefined || condition.max === undefined) {
        return "Min and max values are required for between/not_between operators";
      }
      if (condition.min >= condition.max) {
        return "Min value must be less than max value";
      }
    } else if (condition.operator === MATCH_OPERATORS.IN || condition.operator === MATCH_OPERATORS.NOT_IN) {
      if (!Array.isArray(condition.value) || condition.value.length === 0) {
        return "Value must be a non-empty array for in/not_in operators";
      }
    } else {
      if (condition.value === undefined || condition.value === null || condition.value === '') {
        return "Value is required";
      }
    }

    return null; // Valid
  }

  /**
   * Validate a rule set (pass, refer, fail)
   */
  static validateRuleSet(ruleSet, ruleName) {
    if (!ruleSet.conditions || !Array.isArray(ruleSet.conditions) || ruleSet.conditions.length === 0) {
      return `${ruleName} rule must have at least one condition`;
    }

    if (!ruleSet.logic || !Object.values(LOGICAL_OPERATORS).includes(ruleSet.logic)) {
      return `${ruleName} rule must have a valid logic operator (AND/OR)`;
    }

    // Validate each condition
    for (let i = 0; i < ruleSet.conditions.length; i++) {
      const condition = ruleSet.conditions[i];
      
      if (condition.group) {
        // Validate grouped conditions
        if (!Array.isArray(condition.group) || condition.group.length === 0) {
          return `${ruleName} rule condition ${i + 1}: group must be a non-empty array`;
        }
        
        if (!condition.group_logic || !Object.values(LOGICAL_OPERATORS).includes(condition.group_logic)) {
          return `${ruleName} rule condition ${i + 1}: group must have valid group_logic (AND/OR)`;
        }
        
        for (let j = 0; j < condition.group.length; j++) {
          const groupCondition = condition.group[j];
          const validationError = this.validateCondition(groupCondition);
          if (validationError) {
            return `${ruleName} rule condition ${i + 1}, group item ${j + 1}: ${validationError}`;
          }
        }
      } else {
        // Validate single condition
        const validationError = this.validateCondition(condition);
        if (validationError) {
          return `${ruleName} rule condition ${i + 1}: ${validationError}`;
        }
      }
    }

    return null; // Valid
  }

  /**
   * Validate a complete ruling configuration
   */
  static validateConfiguration(config) {
    const errors = [];

    if (!config.name || config.name.trim().length === 0) {
      errors.push("Configuration name is required");
    }

    if (!config.rules) {
      errors.push("Rules are required");
      return errors;
    }

    // Validate each rule set
    const ruleNames = Object.keys(config.rules);
    for (const ruleName of ruleNames) {
      const ruleSet = config.rules[ruleName];
      const validationError = this.validateRuleSet(ruleSet, ruleName);
      if (validationError) {
        errors.push(validationError);
      }
    }

    // Validate evaluation order
    if (!config.evaluation_order || !Array.isArray(config.evaluation_order)) {
      errors.push("Evaluation order must be an array");
    } else {
      const missingRules = ruleNames.filter(rule => !config.evaluation_order.includes(rule));
      if (missingRules.length > 0) {
        errors.push(`Evaluation order missing rules: ${missingRules.join(', ')}`);
      }
    }

    return errors;
  }

  /**
   * Evaluate a single condition against scorecard data
   */
  static evaluateCondition(condition, scorecardData) {
    const scorecardValue = scorecardData[condition.scorecard];
    
    if (scorecardValue === undefined || scorecardValue === null) {
      return false; // Missing data fails condition
    }

    const { operator, value, min, max, type } = condition;

    if (type === SCORECARD_TYPES.NUMERIC) {
      const numValue = Number(scorecardValue);
      const targetValue = Number(value);

      switch (operator) {
        case NUMERIC_OPERATORS.LESS_THAN:
          return numValue < targetValue;
        case NUMERIC_OPERATORS.LESS_THAN_EQUAL:
          return numValue <= targetValue;
        case NUMERIC_OPERATORS.GREATER_THAN:
          return numValue > targetValue;
        case NUMERIC_OPERATORS.GREATER_THAN_EQUAL:
          return numValue >= targetValue;
        case NUMERIC_OPERATORS.EQUAL:
          return numValue === targetValue;
        case NUMERIC_OPERATORS.NOT_EQUAL:
          return numValue !== targetValue;
        case NUMERIC_OPERATORS.BETWEEN:
          return numValue >= Number(min) && numValue <= Number(max);
        case NUMERIC_OPERATORS.NOT_BETWEEN:
          return !(numValue >= Number(min) && numValue <= Number(max));
        default:
          return false;
      }
    } else if (type === SCORECARD_TYPES.MATCH) {
      const strValue = String(scorecardValue).toLowerCase();

      switch (operator) {
        case MATCH_OPERATORS.EQUALS:
          return strValue === String(value).toLowerCase();
        case MATCH_OPERATORS.NOT_EQUALS:
          return strValue !== String(value).toLowerCase();
        case MATCH_OPERATORS.IN:
          return Array.isArray(value) && 
                 value.some(v => String(v).toLowerCase() === strValue);
        case MATCH_OPERATORS.NOT_IN:
          return Array.isArray(value) && 
                 !value.some(v => String(v).toLowerCase() === strValue);
        default:
          return false;
      }
    }

    return false;
  }

  /**
   * Evaluate a group of conditions with logic
   */
  static evaluateConditionGroup(conditions, logic, scorecardData) {
    if (!conditions || conditions.length === 0) {
      return true; // Empty group is considered true
    }

    const results = conditions.map(condition => {
      if (condition.group) {
        // Recursively evaluate grouped conditions
        return this.evaluateConditionGroup(condition.group, condition.group_logic, scorecardData);
      } else {
        return this.evaluateCondition(condition, scorecardData);
      }
    });

    if (logic === LOGICAL_OPERATORS.AND) {
      return results.every(result => result === true);
    } else if (logic === LOGICAL_OPERATORS.OR) {
      return results.some(result => result === true);
    }

    return false;
  }

  /**
   * Evaluate a complete ruling configuration against scorecard data
   */
  static evaluateConfiguration(config, scorecardData) {
    const evaluationOrder = config.evaluation_order || [RULE_OUTCOMES.PASS, RULE_OUTCOMES.REFER, RULE_OUTCOMES.FAIL];
    
    for (const ruleName of evaluationOrder) {
      const ruleSet = config.rules[ruleName];
      if (ruleSet && this.evaluateConditionGroup(ruleSet.conditions, ruleSet.logic, scorecardData)) {
        return ruleName;
      }
    }

    return config.default_outcome || RULE_OUTCOMES.OTHER;
  }

  /**
   * Create a new condition template
   */
  static createConditionTemplate(scorecardName = 'scorecard_1') {
    const scorecardNum = parseInt(scorecardName.split('_')[1]);
    const type = this.getScorecardType(scorecardNum);
    const operators = this.getAvailableOperators(scorecardNum);
    const defaultOperator = Object.values(operators)[0];

    return {
      scorecard: scorecardName,
      operator: defaultOperator,
      value: type === SCORECARD_TYPES.NUMERIC ? 0 : "",
      type: type
    };
  }

  /**
   * Create a new rule set template
   */
  static createRuleSetTemplate() {
    return {
      conditions: [this.createConditionTemplate()],
      logic: LOGICAL_OPERATORS.AND
    };
  }

  /**
   * Create a new ruling configuration template
   */
  static createConfigurationTemplate() {
    return {
      name: "New Configuration",
      description: "",
      version: "1.0.0",
      is_active: false,
      is_default: false,
      rules: {
        [RULE_OUTCOMES.PASS]: this.createRuleSetTemplate(),
        [RULE_OUTCOMES.REFER]: this.createRuleSetTemplate(),
        [RULE_OUTCOMES.FAIL]: this.createRuleSetTemplate()
      },
      evaluation_order: [RULE_OUTCOMES.PASS, RULE_OUTCOMES.REFER, RULE_OUTCOMES.FAIL],
      default_outcome: RULE_OUTCOMES.OTHER
    };
  }

  /**
   * Convert legacy threshold configuration to ruling configuration
   */
  static convertFromLegacyThresholds(thresholds) {
    const config = this.createConfigurationTemplate();
    config.name = "Converted from Legacy Thresholds";
    config.description = "Auto-converted from existing threshold configuration";

    // Build pass conditions (all scorecards must pass)
    config.rules.pass.conditions = [];
    
    // Build refer conditions (any scorecard refers, but none fail)
    config.rules.refer.conditions = [];
    
    // Build fail conditions (any scorecard fails)
    config.rules.fail.conditions = [];

    for (let i = 1; i <= 4; i++) {
      const scorecardKey = `scorecard_${i}`;
      const scorecardData = thresholds[scorecardKey];
      
      if (!scorecardData) continue;

      if (this.isMatchBased(i)) {
        // Match-based scorecard
        if (scorecardData.pass_set && scorecardData.pass_set.length > 0) {
          config.rules.pass.conditions.push({
            scorecard: scorecardKey,
            operator: MATCH_OPERATORS.IN,
            value: scorecardData.pass_set,
            type: SCORECARD_TYPES.MATCH
          });
        }

        if (scorecardData.fail_set && scorecardData.fail_set.length > 0) {
          config.rules.fail.conditions.push({
            scorecard: scorecardKey,
            operator: MATCH_OPERATORS.IN,
            value: scorecardData.fail_set,
            type: SCORECARD_TYPES.MATCH
          });
        }
      } else {
        // Numeric scorecard
        if (scorecardData.pass_threshold !== undefined) {
          config.rules.pass.conditions.push({
            scorecard: scorecardKey,
            operator: NUMERIC_OPERATORS.GREATER_THAN_EQUAL,
            value: scorecardData.pass_threshold,
            type: SCORECARD_TYPES.NUMERIC
          });
        }

        if (scorecardData.refer_threshold !== undefined && scorecardData.pass_threshold !== undefined) {
          config.rules.refer.conditions.push({
            scorecard: scorecardKey,
            operator: NUMERIC_OPERATORS.BETWEEN,
            min: scorecardData.refer_threshold,
            max: scorecardData.pass_threshold - 0.01,
            type: SCORECARD_TYPES.NUMERIC
          });
        }

        if (scorecardData.refer_threshold !== undefined) {
          config.rules.fail.conditions.push({
            scorecard: scorecardKey,
            operator: NUMERIC_OPERATORS.LESS_THAN,
            value: scorecardData.refer_threshold,
            type: SCORECARD_TYPES.NUMERIC
          });
        }
      }
    }

    return config;
  }

  /**
   * Get user-friendly operator labels
   */
  static getOperatorLabel(operator) {
    const labels = {
      [NUMERIC_OPERATORS.LESS_THAN]: 'Less than (<)',
      [NUMERIC_OPERATORS.LESS_THAN_EQUAL]: 'Less than or equal (≤)',
      [NUMERIC_OPERATORS.GREATER_THAN]: 'Greater than (>)',
      [NUMERIC_OPERATORS.GREATER_THAN_EQUAL]: 'Greater than or equal (≥)',
      [NUMERIC_OPERATORS.EQUAL]: 'Equal to (=)',
      [NUMERIC_OPERATORS.NOT_EQUAL]: 'Not equal to (≠)',
      [NUMERIC_OPERATORS.BETWEEN]: 'Between (inclusive)',
      [NUMERIC_OPERATORS.NOT_BETWEEN]: 'Not between',
      [MATCH_OPERATORS.EQUALS]: 'Equals',
      [MATCH_OPERATORS.NOT_EQUALS]: 'Not equals',
      [MATCH_OPERATORS.IN]: 'Is in list',
      [MATCH_OPERATORS.NOT_IN]: 'Is not in list'
    };

    return labels[operator] || operator;
  }
}

export default RulingConfigUtils;